"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { site, mainNav, services, angebote } from "@/lib/site";
import { industries } from "@/lib/industries";
import { solutions } from "@/lib/solutions";
import { workshops } from "@/lib/workshops";
import { Icon } from "@/components/ui/Icon";
import { ThemeToggle } from "@/components/layout/ThemeToggle";

/** Top-Level-Einträge, die im Mega-Menü stecken und daher nicht zusätzlich in die Leiste gehören. */
const inMegaMenu = new Set(["Leistungen", "Branchen", "Lösungen", "Workshops"]);

/**
 * „Ablauf" und „FAQ" sind Sprungmarken auf die Startseite, keine eigenen
 * Seiten. In der Leiste kosteten sie rund 135 px – genau die Breite, die
 * fehlte, sodass das Logo gestaucht wurde und „Über uns" auf zwei Zeilen
 * umbrach. Beide stehen weiterhin im Footer und im mobilen Menü.
 */
const nichtInLeiste = new Set(["Ablauf", "FAQ"]);

/** „Referenzen" bekommt erst ab 1280 px Platz; darunter führt das Mega-Menü hin. */
const nurAbXl = new Set(["Referenzen"]);

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Menü bei Navigation schließen
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMobileOpen(false);
    setServicesOpen(false);
  }, [pathname]);

  /*
   * Kurzer Nachlauf beim Schließen.
   *
   * Der Weg vom Menüpunkt zu einem Untereintrag führt zwangsläufig über
   * Fläche, die weder zum einen noch zum anderen gehört – etwa wenn man
   * schräg auf eine weit außen liegende Spalte zielt. Ohne Nachlauf
   * schließt das Menü in genau diesem Moment und der Klick geht ins Leere.
   * 140 ms sind lang genug für die Überbrückung und kurz genug, dass ein
   * bewusstes Wegbewegen sich nicht träge anfühlt.
   */
  const schliessTimer = useRef<number | null>(null);

  function menueOeffnen() {
    if (schliessTimer.current !== null) {
      window.clearTimeout(schliessTimer.current);
      schliessTimer.current = null;
    }
    setServicesOpen(true);
  }

  function menueSchliessen({ sofort = false } = {}) {
    if (schliessTimer.current !== null) window.clearTimeout(schliessTimer.current);
    if (sofort) {
      setServicesOpen(false);
      return;
    }
    schliessTimer.current = window.setTimeout(() => setServicesOpen(false), 140);
  }

  useEffect(() => {
    return () => {
      if (schliessTimer.current !== null) window.clearTimeout(schliessTimer.current);
    };
  }, []);

  // Body-Scroll sperren, wenn mobiles Menü offen
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "border-b border-line bg-header backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="container-page flex h-16 items-center justify-between md:h-20">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5" aria-label="WeDev-IT Startseite">
          <Image
            src="/brand/wedev-it-logo.png"
            alt="WeDev-IT"
            width={150}
            height={29}
            priority
            className="h-6 w-auto logo-adaptive md:h-7"
          />
        </Link>

        {/* Desktop-Navigation */}
        {/*
          * `whitespace-nowrap` an jedem Menüpunkt ist hier Pflicht, nicht
          * Kosmetik: In der gefüllten Leiste blieb für „Über uns" zu wenig
          * Platz, der Eintrag brach auf zwei Zeilen um und wurde höher als
          * die Nachbarn. Weil umbruchfreie Punkte nicht mehr schrumpfen
          * können, ist die Innenabstand von px-4 auf px-3 zurückgenommen –
          * damit bleibt zwischen Logo, Leiste und CTA wieder Luft.
          */}
        <nav className="ml-auto hidden items-center gap-0.5 self-stretch pl-6 lg:flex xl:gap-1" aria-label="Hauptnavigation">
          {/* Leistungen mit Dropdown */}
          {/*
            * `self-stretch` ist hier kein Schönheitsfehler, sondern der Kern
            * der Sache: Ohne das reichte der Auslöser nur bis y=58, das Panel
            * begann aber erst bei y=80. Die 22 px dazwischen gehörten zu
            * keinem von beiden – die Maus verließ das Element, `mouseleave`
            * feuerte, und das Menü schloss, bevor man es erreichen konnte.
            * Jetzt reicht der Auslöser bis zur Unterkante der Kopfzeile und
            * grenzt damit unmittelbar an das Panel.
            */}
          <div
            className="relative flex items-center self-stretch"
            onMouseEnter={menueOeffnen}
            onMouseLeave={() => menueSchliessen()}
            onFocus={menueOeffnen}
            onBlur={(e) => {
              // React lässt onBlur aufsteigen; schließen nur, wenn der Fokus
              // das Menü tatsächlich verlassen hat und nicht bloß von einem
              // Untereintrag zum nächsten gewandert ist.
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                menueSchliessen({ sofort: true });
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape" && servicesOpen) menueSchliessen({ sofort: true });
            }}
          >
            <Link
              href="/leistungen"
              className="flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-fg-muted transition-colors hover:text-fg"
            >
              Leistungen
              <Icon name="chevron-down" size={15} className={servicesOpen ? "rotate-180 transition-transform" : "transition-transform"} />
            </Link>
            {servicesOpen && (
              <div
                /*
                 * Am BILDSCHIRM zentrieren, nicht am Menüpunkt.
                 * Vorher war das Panel `absolute` und wurde relativ zum
                 * Auslöser zentriert – da "Leistungen" links in der Leiste
                 * sitzt, ragte das 74rem breite Panel links aus dem Fenster.
                 * `fixed` bezieht sich auf das Ansichtsfenster, dadurch bleibt
                 * es auf jeder Breite vollständig sichtbar.
                 */
                className="fixed left-1/2 top-16 w-[74rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 pt-3 md:top-20"
              >
                <div className="card grid grid-cols-4 gap-5 p-6 shadow-2xl">
                  {/* Spalte 1: Leistungen (mit Beschreibung) */}
                  <div>
                    <Link
                      href="/leistungen"
                      className="mb-2 flex items-center gap-1.5 px-2 text-xs font-semibold uppercase tracking-wider text-brand-ink hover:text-fg"
                    >
                      Leistungen <Icon name="arrow-right" size={13} />
                    </Link>
                    <div className="flex flex-col gap-0.5">
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          href={`/leistungen/${s.slug}`}
                          className="group flex items-start gap-2.5 rounded-lg p-2 transition-colors hover:bg-surface-2"
                        >
                          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-brand-ink">
                            <Icon name={s.icon as never} size={16} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-fg">{s.navTitle}</span>
                            <span className="block text-xs leading-snug text-fg-subtle">{s.tagline}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Spalte 2: Branchen */}
                  <div>
                    <Link
                      href="/branchen"
                      className="mb-2 flex items-center gap-1.5 px-2 text-xs font-semibold uppercase tracking-wider text-brand-ink hover:text-fg"
                    >
                      Branchen <Icon name="arrow-right" size={13} />
                    </Link>
                    <div className="flex flex-col gap-0.5">
                      {industries.map((i) => (
                        <Link
                          key={i.slug}
                          href={`/branchen/${i.slug}`}
                          className="flex items-center gap-2.5 rounded-lg p-2 transition-colors hover:bg-surface-2"
                        >
                          <Icon name={i.icon} size={16} className="shrink-0 text-brand-ink" />
                          <span className="text-sm text-fg-muted">{i.navTitle}</span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Spalte 3: Lösungen */}
                  <div>
                    <Link
                      href="/loesungen"
                      className="mb-2 flex items-center gap-1.5 px-2 text-xs font-semibold uppercase tracking-wider text-brand-ink hover:text-fg"
                    >
                      Lösungen <Icon name="arrow-right" size={13} />
                    </Link>
                    <div className="flex flex-col gap-0.5">
                      {solutions.map((s) => (
                        <Link
                          key={s.slug}
                          href={`/loesungen/${s.slug}`}
                          className="flex items-center gap-2.5 rounded-lg p-2 transition-colors hover:bg-surface-2"
                        >
                          <Icon name={s.icon} size={16} className="shrink-0 text-accent" />
                          <span className="text-sm text-fg-muted">{s.navTitle}</span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Spalte 4: Arbeiten, Pakete & Fixpreise, darunter Workshops */}
                  <div>
                    <Link
                      href="/websites"
                      className="mb-2 flex items-center gap-1.5 px-2 text-xs font-semibold uppercase tracking-wider text-brand-ink hover:text-fg"
                    >
                      Arbeiten <Icon name="arrow-right" size={13} />
                    </Link>
                    <div className="mb-5 flex flex-col gap-0.5">
                      {[
                        { href: "/websites", label: "Websites", hint: "Gebaute Seiten zum Anklicken", icon: "dashboard" as const },
                        { href: "/referenzen", label: "Referenzen", hint: "Projekte im Detail", icon: "star" as const },
                      ].map((a) => (
                        <Link key={a.href} href={a.href} className="flex items-start gap-2.5 rounded-lg p-2 transition-colors hover:bg-surface-2">
                          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-brand-ink">
                            <Icon name={a.icon} size={16} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-fg">{a.label}</span>
                            <span className="block text-xs leading-snug text-fg-subtle">{a.hint}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                    <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-brand-ink">
                      Pakete & Fixpreise
                    </p>
                    <div className="mb-5 flex flex-col gap-0.5">
                      {angebote.map((a) => (
                        <Link
                          key={a.href}
                          href={a.href}
                          className="flex items-start gap-2.5 rounded-lg p-2 transition-colors hover:bg-surface-2"
                        >
                          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-brand-ink">
                            <Icon name={a.icon} size={16} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-fg">{a.label}</span>
                            <span className="block text-xs leading-snug text-fg-subtle">{a.hint}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                    <Link
                      href="/workshops"
                      className="mb-2 flex items-center gap-1.5 px-2 text-xs font-semibold uppercase tracking-wider text-brand-ink hover:text-fg"
                    >
                      Workshops <Icon name="arrow-right" size={13} />
                    </Link>
                    <div className="flex flex-col gap-0.5">
                      {workshops.map((w) => (
                        <Link
                          key={w.slug}
                          href={`/workshops/${w.slug}`}
                          className="flex items-center gap-2.5 rounded-lg p-2 transition-colors hover:bg-surface-2"
                        >
                          <Icon name={w.icon} size={16} className="shrink-0 text-accent" />
                          <span className="text-sm text-fg-muted">{w.navTitle}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {mainNav
            .filter((i) => !inMegaMenu.has(i.label) && !nichtInLeiste.has(i.label) && i.label !== "Kontakt")
            .map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-fg-muted transition-colors hover:text-fg ${nurAbXl.has(item.label) ? "hidden xl:inline-flex" : ""}`}
              >
                {item.label}
              </Link>
            ))}
        </nav>

        {/* CTAs */}
        <div className="hidden items-center gap-2 pl-3 lg:flex xl:pl-5">
          {/* Reihenfolge mit Bedacht: Der Umschalter stand zwischen „Über uns"
              und „Kundenlogin" und riss die Textlinks auseinander. Jetzt
              bilden die Links eine Reihe, dann Umschalter, dann CTA. */}
          <Link
            href="/login"
            className="hidden whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-fg-muted transition-colors hover:text-fg xl:inline-flex"
          >
            Kundenlogin
          </Link>
          <ThemeToggle />
          <Link
            href="/kontakt"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white shadow-cta transition-all hover:bg-brand-400"
          >
            Erstgespräch sichern
            <Icon name="arrow-right" size={17} />
          </Link>
        </div>

        {/* Mobile Toggle */}
        <div className="flex items-center gap-2 lg:hidden">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            /*
              44x44 px ist das von Apple und WCAG 2.2 empfohlene Mindestmaß
              für Tippflächen. Vorher waren es 40x40 mit nur 4 px Abstand zum
              Umschalter daneben – zu klein und zu dicht, um sicher zu treffen.
              -mr-2 zieht die vergrößerte Fläche nach außen, damit die Leiste
              optisch gleich bleibt.
            */
            className="-mr-2 grid h-11 w-11 place-items-center rounded-xl text-fg transition-colors active:bg-surface-2"
            aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={mobileOpen}
          >
            <Icon name={mobileOpen ? "close" : "menu"} size={24} />
          </button>
        </div>
      </div>

      {/* Mobiles Menü */}
      {mobileOpen && (
        <div className="lg:hidden">
          <div className="container-page max-h-[calc(100dvh-4rem)] overflow-y-auto pb-8 pt-2">
            <nav className="flex flex-col gap-1" aria-label="Mobile Navigation">
              <Link
                href="/leistungen"
                className="flex items-center gap-1.5 px-3 pt-3 text-xs font-semibold uppercase tracking-wider text-brand-ink"
              >
                Leistungen <Icon name="arrow-right" size={12} />
              </Link>
              {services.map((s) => (
                <Link
                  key={s.slug}
                  href={`/leistungen/${s.slug}`}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-fg-muted hover:bg-surface-2 hover:text-fg"
                >
                  <Icon name={s.icon as never} size={18} className="text-brand-ink" />
                  {s.navTitle}
                </Link>
              ))}

              <div className="my-2 h-px bg-surface-3" />
              <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-brand-ink">Pakete & Fixpreise</p>
              {angebote.map((a) => (
                <Link
                  key={a.href}
                  href={a.href}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-fg-muted hover:bg-surface-2 hover:text-fg"
                >
                  <Icon name={a.icon} size={18} className="text-brand-ink" />
                  {a.label}
                </Link>
              ))}

              <div className="my-2 h-px bg-surface-3" />
              <Link
                href="/branchen"
                className="flex items-center gap-1.5 px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-brand-ink"
              >
                Branchen <Icon name="arrow-right" size={12} />
              </Link>
              {industries.map((i) => (
                <Link
                  key={i.slug}
                  href={`/branchen/${i.slug}`}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-fg-muted hover:bg-surface-2 hover:text-fg"
                >
                  <Icon name={i.icon} size={18} className="text-brand-ink" />
                  {i.navTitle}
                </Link>
              ))}

              <div className="my-2 h-px bg-surface-3" />
              <Link
                href="/loesungen"
                className="flex items-center gap-1.5 px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-brand-ink"
              >
                Lösungen <Icon name="arrow-right" size={12} />
              </Link>
              {solutions.map((s) => (
                <Link
                  key={s.slug}
                  href={`/loesungen/${s.slug}`}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-fg-muted hover:bg-surface-2 hover:text-fg"
                >
                  <Icon name={s.icon} size={18} className="text-accent" />
                  {s.navTitle}
                </Link>
              ))}

              <div className="my-2 h-px bg-surface-3" />
              <Link
                href="/workshops"
                className="rounded-xl px-3 py-2.5 font-medium text-fg-muted hover:bg-surface-2 hover:text-fg"
              >
                Workshops & Vorträge
              </Link>
              {mainNav
                // NIS2 steht mobil schon unter „Pakete & Fixpreise“.
                .filter((i) => !inMegaMenu.has(i.label) && i.label !== "NIS2")
                .map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="rounded-xl px-3 py-2.5 font-medium text-fg-muted hover:bg-surface-2 hover:text-fg"
                  >
                    {item.label}
                  </Link>
                ))}
              <div className="my-2 h-px bg-surface-3" />
              <Link
                href="/login"
                className="flex items-center gap-2 rounded-xl px-3 py-2.5 font-medium text-fg-muted hover:bg-surface-2 hover:text-fg"
              >
                <Icon name="lock" size={18} /> Kundenlogin
              </Link>
              <Link
                href="/kontakt"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-brand-500 px-5 py-3 font-semibold text-white"
              >
                Erstgespräch sichern
                <Icon name="arrow-right" size={18} />
              </Link>
              <a
                href={site.contact.phoneHref}
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-full border border-line px-5 py-3 font-medium text-fg"
              >
                <Icon name="phone" size={17} /> {site.contact.phone}
              </a>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
